import { useState, useEffect } from "react";
import { PageTitle, SSNToggle } from "../../components";
import { Table, Space, Button, Form, Input, Select, Spin, message } from "antd";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import type { ColumnsType } from "antd/lib/table";
import { CURRENT_CLUB_ID } from "../../_helpers";
import { erembeApi, type ErembeUserData } from "../../_helpers/apiService";

const tailLayout = {
  wrapperCol: { offset: 6, span: 18 },
};

const { Option } = Select;

function Erembe() {
  const [searchForm] = Form.useForm();
  const [archerList, setArcherList] = useState<ErembeUserData[]>([]);
  const [archerOldData, setArcherOldData] = useState<ErembeUserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchKey, setSearchKey] = useState("first_name");
  const [searchValue, setSearchValue] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchErembeData(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const fetchErembeData = async (page: number, size: number) => {
    setLoading(true);
    try {
      // Calculate skip value for pagination
      const skip = (page - 1) * size;

      // Call backend erembe API with pagination - it handles all the sorting!
      const response = await erembeApi.get(CURRENT_CLUB_ID, { skip, limit: size });

      const maleArchers = response.data.male || [];

      setArcherList(maleArchers);
      setArcherOldData(maleArchers);
      setTotal(response.data.total_male || 0);
    } catch (error: any) {
      message.error("Эрэмбийн мэдээлэл татахад алдаа гарлаа");
    } finally {
      setLoading(false);
    }
  };

  const onFinish = () => {
    setLoading(true);

    const filtered: ErembeUserData[] = archerOldData.filter((item: any): boolean => {
      const value = item[searchKey];
      return value
        ?.toString()
        .toLowerCase()
        .includes(searchValue?.toLowerCase());
    });

    setLoading(false);
    setArcherList(filtered);
  };

  const clearSearchForm = () => {
    searchForm.resetFields();
    setSearchValue(null);
    setArcherList(archerOldData);
  };

  const columns: ColumnsType<ErembeUserData> = [
    {
      title: "Эрэмбэ",
      dataIndex: "index",
      key: "index",
      width: 80,
      fixed: 'left',
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Овог",
      dataIndex: "last_name",
      key: "last_name",
      width: 120,
    },
    {
      title: "Нэр",
      dataIndex: "first_name",
      key: "first_name",
      width: 120,
    },
    {
      title: "Регистрийн дугаар",
      dataIndex: "ssn",
      key: "ssn",
      width: 150,
      render: (ssn, record) => <SSNToggle userId={record.id} />,
    },
    {
      title: "Улсын цол",
      dataIndex: "rank_name",
      key: "rank_name",
      width: 180,
      render: (value) => value || "-",
    },
    {
      title: "Улсын цол авсан огноо",
      dataIndex: "rank_date",
      key: "rank_date",
      width: 150,
      render: (value) => {
        if (!value) return "-";
        const parsed = dayjs(value);
        return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "-";
      },
    },
    {
      title: "Баг",
      dataIndex: "team_name",
      key: "team_name",
      width: 200,
      render: (value) => value || "-",
    },
    {
      title: "Имэйл хаяг",
      dataIndex: "email",
      key: "email",
      width: 200,
    },
    {
      title: "Утасны дугаар",
      dataIndex: "phone_number",
      key: "phone_number",
      width: 130,
    },
    {
      title: "Үйлдэл",
      key: "action",
      width: 120,
      fixed: 'right',
      render: (_: any, record: ErembeUserData) => (
        <Space size="middle">
          <Link to={`/archers/${record.id}`}>
            <Button type="link">Дэлгэрэнгүй</Button>
          </Link>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageTitle title="Эрэмбэ" />
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <Form
                layout="inline"
                form={searchForm}
                name="search-form"
                onFinish={onFinish}
                initialValues={{ searchKey: "first_name" }}
              >
                <Form.Item label="Хайх талбар" name="searchKey">
                  <Select
                    placeholder="Талбар сонгох"
                    onChange={(value: string) => setSearchKey(value)}
                    style={{ width: 180 }}
                  >
                    <Option value="first_name">Нэр</Option>
                    <Option value="last_name">Овог</Option>
                    <Option value="ssn">Регистр</Option>
                    <Option value="email">Имэйл</Option>
                    <Option value="phone_number">Утас</Option>
                  </Select>
                </Form.Item>
                <Form.Item label="Хайх утга" name="searchValue">
                  <Input
                    placeholder="Хайх"
                    onChange={(e) => setSearchValue(e.target.value)}
                    style={{ width: 200 }}
                  />
                </Form.Item>
                <Form.Item {...tailLayout}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    style={{ marginRight: 10 }}
                    disabled={!searchValue}
                  >
                    Хайх
                  </Button>
                </Form.Item>
                <Form.Item {...tailLayout}>
                  <Button onClick={clearSearchForm}>Цэвэрлэх</Button>
                </Form.Item>
              </Form>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center p-5">
          <Spin size="large" tip="Уншиж байна...">
            <div style={{ minHeight: "200px" }} />
          </Spin>
        </div>
      ) : (
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h4 className="card-title mb-4">
                  Харваачид ({total})
                </h4>
                <Table
                  columns={columns}
                  dataSource={archerList.map((item, index) => ({
                    ...item,
                    key: item.id,
                    index: (currentPage - 1) * pageSize + index + 1,
                  }))}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: total,
                    showSizeChanger: true,
                    showTotal: (total) => `Нийт: ${total}`,
                    onChange: (page, size) => {
                      setCurrentPage(page);
                      if (size !== pageSize) {
                        setPageSize(size);
                      }
                    },
                  }}
                  scroll={{ x: 1400 }}
                  bordered
                  size="small"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Erembe;
